import { useBuildingsByBounds } from "@inrange/api-clients";
import { useBuildingsInRegionList } from "@inrange/building-manager-api-client";
import {
  Site,
  SiteLocation,
} from "@inrange/building-manager-api-client/models-site";
import { polygon } from "@turf/helpers";
import { LatLngBounds, Map as LeafletMap } from "leaflet";
import { useEffect, useState } from "react";
import { MapboxFeature } from "./AddressBar";
import BuildingMap from "./BuildingMap";
import { MapStylesProd, MapStylesTest } from "./MapStyles";
import {
  BmaBuilding,
  bmaBuildingToOsmBuilding,
  Building,
  isIntersectingWithPolygon,
} from "./siteGeoUtils";

const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

interface SiteListGeoProps {
  sites: Site[];
  selectedOrgs: string[];
  setSiteIdsInRegion: (siteIds: string[]) => void;
  setSitesInViewLoading: (loading: boolean) => void;
}

const SiteListGeo = ({
  sites,
  selectedOrgs,
  setSiteIdsInRegion,
  setSitesInViewLoading,
}: SiteListGeoProps) => {
  // Handle the map
  const [map, setMap] = useState<LeafletMap | undefined>();
  const [mapStyle, setMapStyle] = useState(
    testMode ? MapStylesTest.streets : MapStylesProd.streets
  );
  const [mapBounds, setMapBounds] = useState<LatLngBounds | undefined>();
  const [siteMarkers, setSiteMarkers] = useState<Site[]>(sites);

  const params = new URLSearchParams(window.location.search);
  const testOrgId = params.get("testOrgId");

  useEffect(() => {
    if (!map) return;
    setMapBounds(map.getBounds());
  }, [map]);

  const zoomToSite = (site: Site | SiteLocation) => {
    if (!map) return;
    const lat =
      typeof site.latitude === "string"
        ? parseFloat(site.latitude)
        : site.latitude;
    const lng =
      typeof site.longitude === "string"
        ? parseFloat(site.longitude)
        : site.longitude;
    map.flyTo([lat, lng], 16, {
      animate: true,
      duration: 1,
    });
  };

  // Handle the buldings in the map - both site buildings and buildings in bounds
  const [networkBuildingsInBounds, setNetworkBuildingsInBounds] = useState<
    Record<string, Building & { siteIDs?: string[] }>
  >({}); // These buildings owned by other sites
  const [availableBuildingsInBounds, setAvailableBuildingsInBounds] = useState<
    Record<string, Building>
  >({}); // These are selectable polygons
  const [buildingIntersections, setBuildingIntersections] = useState<
    Record<string, boolean>
  >({}); // Object to store a boolean for each building in bounds to check if it intersects with the site

  const fetchAvailableBuildingsInBounds = useBuildingsByBounds(mapBounds);
  const fetchNetworkBuildingsInBounds = useBuildingsInRegionList(
    mapBounds,
    true,
    testOrgId
  );

  useEffect(() => {
    let validOrgs = selectedOrgs;

    if (testOrgId) {
      validOrgs = [...validOrgs, testOrgId];
    }

    if (selectedOrgs.length > 0) {
      setSiteMarkers(
        sites.filter((site) =>
          site.siteOwnerships?.some((ownership) =>
            validOrgs.includes(ownership.orgID)
          )
        )
      );
    } else {
      setSiteMarkers(sites);
    }
  }, [testOrgId, selectedOrgs, sites]);

  useEffect(() => {
    // isInitialLoading, not isLoading: useBuildingsInRegionList is disabled until
    // mapBounds is set, and in @tanstack/react-query v4 a disabled query reports
    // isLoading: true (v3 reported idle/false), which would leave the "sites in
    // view" spinner stuck on before the map first sets its bounds.
    if (fetchNetworkBuildingsInBounds.isInitialLoading) {
      setSitesInViewLoading(true);
    }

    if (!fetchNetworkBuildingsInBounds.data) return;

    setSitesInViewLoading(false);

    const siteIdsInRegion = (fetchNetworkBuildingsInBounds.data as any).sites;
    setSiteIdsInRegion(siteIdsInRegion);

    if (!fetchAvailableBuildingsInBounds.data) return;

    const networkBuildings: Record<string, Building & { siteIDs?: string[] }> =
      {};

    Object.keys((fetchNetworkBuildingsInBounds.data as any).buildings).forEach(
      (key) => {
        const building: BmaBuilding & { siteIDs?: string[] } = (
          fetchNetworkBuildingsInBounds.data as any
        ).buildings[key];
        networkBuildings[key] = {
          ...bmaBuildingToOsmBuilding(building),
          siteIDs: building.siteIDs, // so that we can populate the popup
        };
      }
    );

    const availableBuildings: Record<string, Building> = {};

    const networkBuildingValues = Object.values(networkBuildings);
    const networkBuildingPolygons = Object.values(networkBuildingValues).map(
      (building) => {
        const coordinates = building.geometry.coordinates[0];
        if (coordinates[0][0] !== coordinates[coordinates.length - 1][0]) {
          // If the polygon is not closed, close it
          coordinates.push(coordinates[0]);
        }
        return polygon([coordinates]);
      }
    );

    Object.keys(fetchAvailableBuildingsInBounds.data).forEach((key) => {
      if (buildingIntersections[key] === undefined) {
        buildingIntersections[key] = isIntersectingWithPolygon(
          fetchAvailableBuildingsInBounds.data[key],
          networkBuildingPolygons
        );
      }
      if (!buildingIntersections[key]) {
        availableBuildings[key] = fetchAvailableBuildingsInBounds.data[key];
      }
    });

    setBuildingIntersections(buildingIntersections);
    setAvailableBuildingsInBounds(availableBuildings);
    setNetworkBuildingsInBounds(networkBuildings);
  }, [
    setSitesInViewLoading,
    setSiteIdsInRegion,
    sites,
    buildingIntersections,
    fetchAvailableBuildingsInBounds.data,
    fetchNetworkBuildingsInBounds.data,
    fetchNetworkBuildingsInBounds.isInitialLoading,
    mapBounds,
  ]);

  // Address Bar
  const addressCallback = (suggestion: MapboxFeature) => {
    if (!map) return;
    if (suggestion.bbox) {
      map.fitBounds([
        [suggestion.bbox[1], suggestion.bbox[0]],
        [suggestion.bbox[3], suggestion.bbox[2]],
      ]);
    } else {
      const [lng, lat] = suggestion.center;
      map.setView([lat, lng], 16);
    }
  };

  return (
    <SiteListGeoView
      sites={sites}
      siteMarkers={siteMarkers}
      addressCallback={addressCallback}
      buildingsInBounds={availableBuildingsInBounds}
      networkBuildingsInBounds={networkBuildingsInBounds}
      map={map}
      mapStyle={mapStyle}
      setMap={setMap}
      setMapBounds={setMapBounds}
      setMapStyle={setMapStyle}
      zoomToSite={zoomToSite}
    />
  );
};

export default SiteListGeo;

interface SiteListGeoViewProps {
  sites: Site[];
  siteMarkers: Site[];
  addressCallback: (suggestion: MapboxFeature) => void;
  buildingsInBounds: Record<string, Building>;
  networkBuildingsInBounds: Record<string, Building & { siteIDs?: string[] }>;
  map: LeafletMap | undefined;
  mapStyle: string;
  setMap: (map: LeafletMap) => void;
  setMapBounds: (bounds: LatLngBounds) => void;
  setMapStyle: (style: string) => void;
  zoomToSite: (site: Site | SiteLocation) => void;
}

const SiteListGeoView = ({
  sites,
  siteMarkers,
  addressCallback,
  buildingsInBounds,
  networkBuildingsInBounds,
  map,
  mapStyle,
  setMap,
  setMapBounds,
  setMapStyle,
  zoomToSite,
}: SiteListGeoViewProps) => {
  return (
    <BuildingMap
      sites={sites}
      siteMarkers={siteMarkers}
      map={map}
      mapStyle={mapStyle}
      setMapStyle={setMapStyle}
      setMap={setMap}
      isSiteList={true}
      siteBuildings={{}}
      buildingsInBounds={buildingsInBounds}
      networkBuildingsInBounds={networkBuildingsInBounds}
      addressCallback={addressCallback}
      setMapBounds={setMapBounds}
      mapHeight={"600px"}
      zoomToSite={zoomToSite}
    />
  );
};
